import { PageSchema, type Component, type ComponentJson, type Page, type PageJson } from "#gen/pages/v2/page_pb.js";
import { DEFAULT_HBOX_CONFIG, DEFAULT_INNER_VBOX_CONFIG, DEFAULT_TOP_LEVEL_VBOX_CONFIG, FULL_WIDTH_COMPONENTS, RESTRICTED_NESTED_COMPONENTS } from "#lib/components/page/editing/constants.js";
import type { VBoxWrapTarget } from "#lib/components/page/editing/types.js";
import type { ComponentDefMap } from "#lib/components/page/registry.js";
import { deepClone } from "#lib/deepClone.js";
import { fromJson, toJson } from "#lib/protobuf-compat.js";
import { stringValue, structValue } from "#lib/struct.js";

// Matches the default hbox config and any customization that keeps the col direction with a grid class. A
// spacing or alignment edit swaps its own token but leaves the grid tokens in place, so the grid check
// still holds. The collapse helper uses loose matching because a single-child hbox no longer has a row to display.
function isHBoxDirectionConfig(configValue: unknown): boolean {
	if (configValue == null || typeof configValue !== "object" || Array.isArray(configValue)) return false;
	return (configValue as Record<string, unknown>).direction === DEFAULT_HBOX_CONFIG.direction;
}

function isVBoxDirectionConfig(configValue: unknown): boolean {
	if (configValue == null || typeof configValue !== "object" || Array.isArray(configValue)) return false;
	return (configValue as Record<string, unknown>).direction === DEFAULT_TOP_LEVEL_VBOX_CONFIG.direction;
}

function isDefaultConfig(configValue: unknown, defaults: Record<string, unknown>): boolean {
	if (configValue == null || typeof configValue !== "object" || Array.isArray(configValue)) return false;
	const o = configValue as Record<string, unknown>;
	const defaultKeys = Object.keys(defaults);
	if (Object.keys(o).length !== defaultKeys.length) return false;
	for (const k of defaultKeys) {
		if (o[k] !== defaults[k]) return false;
	}
	return true;
}

export function stripConfigKeys(config: Record<string, unknown>, ids: readonly string[]): Record<string, unknown> {
	if (ids.length === 0) return config;
	const next = { ...config };
	for (const id of ids) {
		delete next[id];
	}
	return next;
}

export function removeEmptyContainersInJsonTree(roots: ComponentJson[], definitions: ComponentDefMap): { components: ComponentJson[]; removedIds: string[] } {
	const removedIds: string[] = [];

	function processNode(node: ComponentJson): ComponentJson | null {
		const def = node.typeUri ? definitions[node.typeUri] : undefined;
		const rawChildren = node.children ?? [];
		const processed = rawChildren.map(processNode).filter((c): c is ComponentJson => c !== null);
		if (def?.isContainer && processed.length === 0) {
			if (node.id) removedIds.push(node.id);
			return null;
		}
		return { ...node, children: processed };
	}

	const out: ComponentJson[] = [];
	for (const r of roots) {
		const p = processNode(r);
		if (p) out.push(p);
	}
	return { components: out, removedIds };
}

// Drops single-child boxes that stopped earning their spot. A one-child h-box has no row to show, a
// one-child inner v-box nothing to stack. The page's own vbox shares the v-box direction but has to
// survive with a single child, so isRoot keeps it safe.
export function collapseSingleChildBoxesInJsonTree(roots: ComponentJson[], pageConfig: Record<string, unknown> | undefined, definitions: ComponentDefMap): { components: ComponentJson[]; unwrappedIds: string[] } {
	const unwrappedIds: string[] = [];

	function isInnerVBoxNode(node: ComponentJson): boolean {
		const def = node.typeUri ? definitions[node.typeUri] : undefined;
		return Boolean(def?.isContainer && node.id && isVBoxDirectionConfig(pageConfig?.[node.id]));
	}

	// What `node` turns into in its parent's list. A collapsing container hands back its lone child.
	function processNode(node: ComponentJson, isRoot: boolean): ComponentJson[] {
		const def = node.typeUri ? definitions[node.typeUri] : undefined;
		const children = (node.children ?? []).flatMap(child => processNode(child, false));
		const id = node.id;
		if (def?.isContainer && id && children.length === 1) {
			const config = pageConfig?.[id];
			const only = children[0]!;
			if (isHBoxDirectionConfig(config)) {
				unwrappedIds.push(id);
				// If the h-box collapses, its surviving v-box child lands at the page top level, where a v-box
				// buys nothing. Unwrap that one too so its leaves drop in as plain rows.
				if (isInnerVBoxNode(only) && only.id) {
					unwrappedIds.push(only.id);
					return only.children ?? [];
				}
				return [only];
			}
			if (!isRoot && isVBoxDirectionConfig(config)) {
				unwrappedIds.push(id);
				return [only];
			}
		}
		return [{ ...node, children }];
	}

	return { components: roots.flatMap(root => processNode(root, true)), unwrappedIds };
}

// A v-box only earns its place inside an h-box cell, where it stacks leaves into a column. As a page row
// or a direct child of the top-level vbox it does nothing, so splice its children up into the parent.
// Leave roots alone. A lone root v-box is the page wrapper itself.
export function flattenMisplacedVBoxesInJsonTree(roots: ComponentJson[], pageConfig: Record<string, unknown> | undefined, definitions: ComponentDefMap): { components: ComponentJson[]; unwrappedIds: string[] } {
	const unwrappedIds: string[] = [];

	function isHbox(node: ComponentJson): boolean {
		const def = node.typeUri ? definitions[node.typeUri] : undefined;
		return Boolean(def?.isContainer && node.id && isHBoxDirectionConfig(pageConfig?.[node.id]));
	}
	function isInnerVbox(node: ComponentJson): boolean {
		const def = node.typeUri ? definitions[node.typeUri] : undefined;
		return Boolean(def?.isContainer && node.id && isVBoxDirectionConfig(pageConfig?.[node.id]));
	}

	// What `node` turns into in its parent's list. A misplaced v-box hands back its own children.
	function processNode(node: ComponentJson, parentIsHbox: boolean): ComponentJson[] {
		const children = (node.children ?? []).flatMap(child => processNode(child, isHbox(node)));
		if (!parentIsHbox && isInnerVbox(node)) {
			unwrappedIds.push(node.id!);
			return children;
		}
		return [{ ...node, children }];
	}

	// Never flatten a root since it is the page wrapper, but do walk its descendants.
	const components = roots.map(root => ({ ...root, children: (root.children ?? []).flatMap(child => processNode(child, isHbox(root))) }));
	return { components, unwrappedIds };
}

// Puts the tree back to a legal shape after a mutation. Collapse single-child boxes, flatten any v-box
// that drifted outside an h-box, then drop empty containers. Returns the ids it removed so the caller
// can strip their config.
export function normalizeLayoutTree(roots: ComponentJson[], pageConfig: Record<string, unknown> | undefined, definitions: ComponentDefMap): { components: ComponentJson[]; removedIds: string[] } {
	const collapsed = collapseSingleChildBoxesInJsonTree(roots, pageConfig, definitions);
	const flattened = flattenMisplacedVBoxesInJsonTree(collapsed.components, pageConfig, definitions);
	const pruned = removeEmptyContainersInJsonTree(flattened.components, definitions);
	return { components: pruned.components, removedIds: [...collapsed.unwrappedIds, ...flattened.unwrappedIds, ...pruned.removedIds] };
}

function nextPrefixedComponentId(prefixLower: string, isTaken: (id: string) => boolean): string {
	const suffix = /^(.+)-(\d+)$/.exec(prefixLower);
	if (suffix) {
		const base = suffix[1];
		let i = Number.parseInt(suffix[2], 10) + 1;
		if (Number.isFinite(i)) {
			let id = `${base}-${i}`;
			while (isTaken(id)) {
				id = `${base}-${++i}`;
			}
			return id;
		}
	}
	let id = `${prefixLower}-1`;
	let i = 1;
	while (isTaken(id)) {
		id = `${prefixLower}-${++i}`;
	}
	return id;
}

export function generateComponentId(page: Page, componentId: string): string {
	const prefix = componentId.toLowerCase();
	return nextPrefixedComponentId(prefix, candidate => Boolean(page.config?.[candidate]));
}

export function removeComponentFromPageTree(roots: ComponentJson[], targetId: string, definitions: ComponentDefMap, pageConfig?: Record<string, unknown>): { components: ComponentJson[]; removedConfigIds: string[]; found: boolean } {
	const removedConfigIds: string[] = [];
	function markConfigRemoved(id: string | undefined) {
		if (id && !removedConfigIds.includes(id)) {
			removedConfigIds.push(id);
		}
	}
	let found = false;

	function markSubtreeConfigRemoved(root: ComponentJson) {
		if (root.id) {
			markConfigRemoved(root.id);
		}
		for (const child of root.children ?? []) {
			markSubtreeConfigRemoved(child);
		}
	}

	function removeTarget(node: ComponentJson): ComponentJson[] {
		if (node.id === targetId) {
			found = true;
			markSubtreeConfigRemoved(node);
			return [];
		}
		const def = node.typeUri ? definitions[node.typeUri] : undefined;
		const children = node.children ?? [];
		if (!def?.isContainer || children.length === 0) {
			return [node];
		}
		const newChildren: ComponentJson[] = [];
		for (const child of children) {
			newChildren.push(...removeTarget(child));
		}
		if (newChildren.length === children.length && newChildren.every((c, i) => c === children[i])) {
			return [node];
		}
		if (newChildren.length === 0) {
			markConfigRemoved(node.id);
			return [];
		}
		return [{ ...node, children: newChildren }];
	}
	const out: ComponentJson[] = [];
	for (const root of roots) {
		out.push(...removeTarget(root));
	}
	if (!found || !pageConfig) {
		return { components: out, removedConfigIds, found };
	}
	const { components: normalized, removedIds } = normalizeLayoutTree(out, pageConfig, definitions);
	for (const id of removedIds) {
		markConfigRemoved(id);
	}
	return { components: normalized, removedConfigIds, found };
}

export type Located<T> = {
	node: T;
	parent: T | undefined;
	parentId: string | undefined;
	index: number;
	siblings: T[];
};

export function locate<T extends { id?: string; children?: T[] }>(roots: T[], id: string, parent?: T): Located<T> | undefined {
	for (let index = 0; index < roots.length; index++) {
		const node = roots[index]!;
		if (node.id === id) {
			return { node, parent, parentId: parent?.id, index, siblings: roots };
		}
		const found = locate(node.children ?? [], id, node);
		if (found) return found;
	}
	return undefined;
}

export function isTopLevelPageVboxContainer(componentId: string, page: Page | undefined): boolean {
	if (!page) return false;
	const config = page.config?.[componentId];
	if (!config) return false;
	if (typeof config !== "object" || Array.isArray(config)) return false;
	return (config as Record<string, unknown>).direction === DEFAULT_TOP_LEVEL_VBOX_CONFIG.direction && page.components.length === 1 && page.components[0]?.id === componentId;
}

// Inverse of wrapInTopLevelContainer. Unwraps a default top-level vbox down to its single child. Customized vboxes are preserved because the user may have set page-level spacing or alignment intentionally. The asymmetry with hbox collapse is intentional. An hbox without two children has no row to render, but a vbox with one child still functions as a container.
export function unwrapRedundantTopLevelVbox(page: Page): Page {
	if (page.components.length !== 1) return page;
	const root = page.components[0]!;
	if (!isTopLevelPageVboxContainer(root.id, page)) return page;
	if (root.children.length !== 1) return page;
	if (!isDefaultConfig(page.config?.[root.id], DEFAULT_TOP_LEVEL_VBOX_CONFIG)) return page;
	const pageJson = toJson(PageSchema, page);
	const nextConfig = { ...(pageJson.config ?? {}) } as Record<string, unknown>;
	delete nextConfig[root.id];
	const onlyChild = (pageJson.components ?? [])[0]?.children?.[0];
	if (!onlyChild) return page;
	return fromJson(PageSchema, {
		...pageJson,
		components: [onlyChild],
		config: nextConfig as NonNullable<PageJson["config"]>,
	});
}

export function duplicateComponent(page: Page, componentId: string): Page {
	const pageJson = toJson(PageSchema, page);
	const roots = (pageJson.components ?? []) as ComponentJson[];
	const baseConfig = { ...pageJson.config } as Record<string, unknown>;

	const source = cloneSubtreeFromComponents(roots, componentId);
	if (!source) {
		return page;
	}

	const location = locate(roots, componentId);
	if (!location) {
		return page;
	}

	const used = new Set(Object.keys(baseConfig));
	const idMap: Map<string, string> = new Map();
	buildSubtreeIdMap(source, used, idMap);

	const duplicated = remapComponentTree(source, idMap);

	const parent = location.parent;
	const parentId = parent?.id;
	const parentIsHbox = parentId ? isHBoxDirectionConfig(baseConfig[parentId]) : false;
	// A stacked column duplicates into a new column beside itself so the copy keeps its stack, instead of
	// spilling its leaves out as loose rows once the tree normalizes. Any other cell copies down as a new row
	// under its container.
	const duplicateColumnInPlace = parentIsHbox && isVBoxDirectionConfig(baseConfig[componentId]);
	const insertAfterId = duplicateColumnInPlace ? componentId : parentIsHbox && location.siblings.length > 1 && parentId ? parentId : componentId;
	const nextRoots = insertDuplicateAfter(roots, insertAfterId, duplicated);

	if (!nextRoots) {
		return page;
	}

	const nextConfig = { ...baseConfig } as NonNullable<PageJson["config"]>;
	const nextConfigRecord = nextConfig as Record<string, unknown>;

	for (const [oldId, newId] of idMap) {
		const v = baseConfig[oldId];
		if (v !== undefined) {
			nextConfigRecord[newId] = deepClone(v);
		}
	}

	return fromJson(PageSchema, {
		...pageJson,
		components: nextRoots,
		config: nextConfig,
	});
}

function insertDuplicateAfter(components: ComponentJson[], targetId: string, duplicated: ComponentJson): ComponentJson[] | undefined {
	const targetIndex = components.findIndex(component => component.id === targetId);
	if (targetIndex !== -1) {
		return [...components.slice(0, targetIndex + 1), duplicated, ...components.slice(targetIndex + 1)];
	}
	let changed = false;
	const next = components.map(component => {
		if (!component.children?.length) {
			return component;
		}
		const nextChildren = insertDuplicateAfter(component.children, targetId, duplicated);
		if (!nextChildren) {
			return component;
		}
		changed = true;
		return {
			...component,
			children: nextChildren,
		};
	});

	return changed ? next : undefined;
}

function cloneSubtreeById(node: ComponentJson, id: string): ComponentJson | undefined {
	if (node.id === id) return deepClone(node);
	for (const child of node.children ?? []) {
		const found = cloneSubtreeById(child, id);
		if (found) {
			return found;
		}
	}
	return undefined;
}

function cloneSubtreeFromComponents(components: ComponentJson[], id: string): ComponentJson | undefined {
	for (const comp of components) {
		const copy = cloneSubtreeById(comp, id);
		if (copy) {
			return copy;
		}
	}
	return undefined;
}

function allocateUniqueId(used: Set<string>, hint: string): string {
	const prefix = hint.toLowerCase();
	const id = nextPrefixedComponentId(prefix, candidate => used.has(candidate));
	used.add(id);
	return id;
}

function buildSubtreeIdMap(node: ComponentJson, used: Set<string>, map: Map<string, string>) {
	if (node.id) {
		map.set(node.id, allocateUniqueId(used, node.id));
	}
	for (const child of node.children ?? []) buildSubtreeIdMap(child, used, map);
}

function remapComponentTree(node: ComponentJson, map: Map<string, string>): ComponentJson {
	const id = node.id ? (map.get(node.id) ?? node.id) : node.id;
	return {
		...deepClone(node),
		id,
		children: (node.children ?? []).map(c => remapComponentTree(c, map)),
	};
}

export function isFormComponentDef(uri: string | undefined, definitions: ComponentDefMap): boolean {
	if (!uri) {
		return false;
	}
	const def = definitions[uri];
	return Boolean(def?.componentType === "Form");
}

// Component types that record a page's conversion. Only one allowed per page.
const CONVERSION_COMPONENT_TYPES = new Set(["Form", "ContactCollection", "Poll"]);

export function isConversionComponentDef(uri: string | undefined, definitions: ComponentDefMap): boolean {
	if (!uri) {
		return false;
	}
	const def = definitions[uri];
	return Boolean(def && CONVERSION_COMPONENT_TYPES.has(def.componentType));
}

// Component types whose content is managed elsewhere (the Content tab, driven by data properties), so
// they have no in-place configuration and can't be added, removed, or duplicated on the page.
const READ_ONLY_COMPONENT_TYPES = new Set(["PropertyList"]);

export function isReadOnlyComponentDef(uri: string | undefined, definitions: ComponentDefMap): boolean {
	if (!uri) {
		return false;
	}
	const def = definitions[uri];
	return Boolean(def && READ_ONLY_COMPONENT_TYPES.has(def.componentType));
}

export function pageHasConversionComponent(page: Page, definitions: ComponentDefMap): boolean {
	function walk(components: Component[]): boolean {
		return components.some(component => isConversionComponentDef(component.typeUri, definitions) || walk(component.children ?? []));
	}
	return walk(page.components);
}

// True for a legacy (non-connectable) form, which navigates forward at the page level rather than from its own handle.
function isPageLevelLinkComponentDef(uri: string | undefined, definitions: ComponentDefMap): boolean {
	return isConversionComponentDef(uri, definitions) && !(uri && definitions[uri]?.isConnectable);
}

export function pageHasPageLevelLinkComponent(page: Page, definitions: ComponentDefMap): boolean {
	function walk(components: Component[]): boolean {
		return components.some(component => isPageLevelLinkComponentDef(component.typeUri, definitions) || walk(component.children ?? []));
	}
	return walk(page.components);
}

export type NewBox = { id: string; kind: "h" | "v" };

export function insertComponentAtSlot(roots: ComponentJson[], parentId: string | undefined, vertIndex: number, horizontalIndex: number | undefined, node: ComponentJson, page: Page, createBox: (row: ComponentJson[], page: Page, direction: "h" | "v") => ComponentJson | undefined, wrap?: VBoxWrapTarget): { components: ComponentJson[]; newBoxIds: NewBox[] } | undefined {
	const newBoxIds: NewBox[] = [];

	if (wrap) {
		let wrapped = false;
		function wrapNode(component: ComponentJson): ComponentJson {
			if (component.id === wrap!.anchorId) {
				const anchor = deepClone(component) as ComponentJson;
				const added = deepClone(node) as ComponentJson;
				const stack = wrap!.position === "above" ? [added, anchor] : [anchor, added];
				const vbox = createBox(stack, page, "v");
				if (!vbox?.id) return component;
				newBoxIds.push({ id: vbox.id, kind: "v" });
				wrapped = true;
				return vbox;
			}
			if (!component.children?.length) return component;
			return { ...component, children: component.children.map(wrapNode) };
		}
		const components = roots.map(wrapNode);
		return wrapped ? { components, newBoxIds } : undefined;
	}

	function insertInto(siblings: ComponentJson[], parentIsHbox: boolean): ComponentJson[] | undefined {
		if (horizontalIndex !== undefined) {
			if (parentIsHbox) {
				// The parent already lays its children out in a row, so a horizontal insert just adds another
				// cell beside the anchor. horizontalIndex is 0 for the anchor's left edge and 1 for its right.
				const at = Math.min(Math.max(vertIndex + horizontalIndex, 0), siblings.length);
				const next = [...siblings];
				next.splice(at, 0, deepClone(node) as ComponentJson);
				return next;
			}
			// The anchor is a lone leaf, so wrap it and the new node together into a fresh two-cell h-box.
			const existing = siblings[vertIndex];
			if (!existing) return undefined;
			const row = [deepClone(existing) as ComponentJson];
			row.splice(Math.min(Math.max(horizontalIndex, 0), row.length), 0, deepClone(node) as ComponentJson);
			const hbox = createBox(row, page, "h");
			if (!hbox?.id) return undefined;
			newBoxIds.push({ id: hbox.id, kind: "h" });
			const next = [...siblings];
			next[vertIndex] = hbox;
			return next;
		}
		const next = [...siblings];
		next.splice(vertIndex, 0, deepClone(node) as ComponentJson);
		return next;
	}

	if (!parentId) {
		const next = insertInto(roots, false);
		return next ? { components: next, newBoxIds } : undefined;
	}

	const parentIsHbox = stringValue(structValue(page?.config, parentId), "direction") === DEFAULT_HBOX_CONFIG.direction;
	let found = false;
	let failed = false;
	function mapNode(component: ComponentJson): ComponentJson {
		if (component.id === parentId) {
			const nextChildren = insertInto(component.children ?? [], parentIsHbox);
			if (!nextChildren) {
				failed = true;
				return component;
			}
			found = true;
			return { ...component, children: nextChildren };
		}
		if (!component.children?.length) return component;
		return { ...component, children: component.children.map(mapNode) };
	}

	const components = roots.map(mapNode);
	if (!found || failed) return undefined;
	return { components, newBoxIds };
}

// Config for boxes an insert just spun up (h-box for a horizontal wrap, inner v-box for a vertical one).
// Built here next to the insert so add and move stay in sync.
export function configForNewBoxes(newBoxIds: NewBox[]): Record<string, { direction: string; class: string }> {
	return Object.fromEntries(newBoxIds.map(box => [box.id, box.kind === "h" ? { ...DEFAULT_HBOX_CONFIG } : { ...DEFAULT_INNER_VBOX_CONFIG }]));
}

function removeComponentById(components: ComponentJson[], id: string): ComponentJson[] {
	return components.filter(component => component.id !== id).map(component => ({ ...component, children: removeComponentById(component.children ?? [], id) }));
}

// The pure half of a drag-to-slot drop. Pull the dragged node out, drop it back at the target slot
// (plain splice, horizontal wrap, or vertical wrap), then re-normalize. Returns the new tree and config.
// The page-root wrap is still the caller's job (normalizePageRoots).
export function moveComponentToSlot(
	pageJson: PageJson,
	page: Page,
	definitions: ComponentDefMap,
	target: { draggedId: string; verticalIndex: number; horizontalIndex?: number; parentId?: string; wrap?: VBoxWrapTarget },
	createBox: (row: ComponentJson[], page: Page, direction: "h" | "v") => ComponentJson | undefined,
): { components: ComponentJson[]; config: NonNullable<PageJson["config"]> } | undefined {
	const components = (pageJson.components ?? []) as ComponentJson[];
	const source = locate(components, target.draggedId);
	if (!source) return undefined;
	const dragged = deepClone(source.node) as ComponentJson;
	const withoutDragged = removeComponentById(components, target.draggedId);
	// Splicing back into the same parent shifts the target down by one if the dragged node sat ahead of it.
	// A wrap is keyed by id, so the index means nothing and we skip the fixup.
	const adjustedVerticalIndex = !target.wrap && source.parentId === (target.parentId ?? undefined) && source.index < target.verticalIndex ? target.verticalIndex - 1 : target.verticalIndex;
	const inserted = insertComponentAtSlot(withoutDragged, target.parentId, adjustedVerticalIndex, target.horizontalIndex, dragged, page, createBox, target.wrap);
	if (!inserted) return undefined;
	const nextConfig = { ...(pageJson.config ?? {}), ...configForNewBoxes(inserted.newBoxIds) } as Record<string, unknown>;
	const normalized = normalizeLayoutTree(inserted.components, nextConfig, definitions);
	const cleanedConfig = stripConfigKeys(nextConfig, normalized.removedIds) as NonNullable<PageJson["config"]>;
	return { components: normalized.components, config: cleanedConfig };
}

function isComponentHboxContainer(component: Component, page: Page): boolean {
	return stringValue(structValue(page?.config, component.id), "direction") === "col";
}

export type InsertionSide = "top" | "bottom" | "left" | "right";
export type InsertionCandidate = { id: string; depth: number; rect: { top: number; bottom: number; left: number; right: number }; placements: { above: boolean; below: boolean; left: boolean; right: boolean }; belowFill?: { left: number; right: number; top: number; bottom: number } | undefined; aboveFill?: { left: number; right: number; top: number } | undefined };

// The region a stack's last child claims for an append drop. A vertical stack stretches to its row height, so a
// short column leaves empty space under its last child. That space should append to the column, not catch the
// row-grow edge, so hand back the parent's box when there is a gap under the child. Returns nothing for h-box
// cells (which fill their grid cell) and non-last children.
export function belowFillRegion(placesBelow: boolean, parentDirection: string | undefined, childBottom: number, parentRect: { left: number; right: number; bottom: number } | undefined): { left: number; right: number; top: number; bottom: number } | undefined {
	if (!placesBelow || parentDirection === DEFAULT_HBOX_CONFIG.direction || !parentRect || parentRect.bottom <= childBottom + 1) return undefined;
	return { left: parentRect.left, right: parentRect.right, top: childBottom, bottom: parentRect.bottom };
}

// The empty space a short leaf cell leaves below its content. A lone image beside a taller column stretches to the
// row height, so its content sits at the top and the rest of the cell is empty. That space wraps the leaf into a
// column, so route it there. Measured off the content itself since the cell box reaches the whole row bottom.
function wrapCellFillRegion(cell: HTMLElement, cellRect: { left: number; right: number; bottom: number }): { left: number; right: number; top: number; bottom: number } | undefined {
	let contentBottom = -Infinity;
	for (const node of cell.querySelectorAll("img, svg, video, picture, canvas, p, a, span, button, label, input, h1, h2, h3, h4, h5, h6")) {
		if (node.classList.contains("cursor-grab") || node.closest("[data-insert-slot]")) continue;
		const b = node.getBoundingClientRect();
		if (b.height > 0 && b.bottom > contentBottom) contentBottom = b.bottom;
	}
	if (contentBottom === -Infinity || cellRect.bottom <= contentBottom + 1) return undefined;
	return { left: cellRect.left, right: cellRect.right, top: contentBottom, bottom: cellRect.bottom };
}

// The mirror of belowFillRegion for the top. A stack aligned to its bottom or center leaves empty space above its
// first child. That space should prepend to the stack, so hand back the parent's box when there is a gap over the
// child. Returns nothing for a top-aligned stack (no gap) or an h-box cell.
export function aboveFillRegion(placesAbove: boolean, parentDirection: string | undefined, childTop: number, parentRect: { left: number; right: number; top: number } | undefined): { left: number; right: number; top: number } | undefined {
	if (!placesAbove || parentDirection === DEFAULT_HBOX_CONFIG.direction || !parentRect || childTop <= parentRect.top + 1) return undefined;
	return { left: parentRect.left, right: parentRect.right, top: parentRect.top };
}

// Builds the drop-target candidates for one hit-test pass over the rendered page. Add mode and drag mode share
// this so their geometry, depth, placements, and append regions can never drift apart. draggedId skips the
// dragged node and shapes its placements, excludeSubtreeOf skips the dragged element's floating subtree,
// blockHorizontal drops the row-grow edges for a type that can never be a row cell, and canNest allows the
// below-append region only for a type that may nest.
export function collectInsertionCandidates(root: ParentNode, page: Page, definitions: ComponentDefMap, opts: { draggedId?: string; excludeSubtreeOf?: HTMLElement | null; blockHorizontal?: boolean; canNest?: boolean } = {}): InsertionCandidate[] {
	const { draggedId, excludeSubtreeOf, blockHorizontal = false, canNest = true } = opts;
	const candidates: InsertionCandidate[] = [];
	// Marks each parent once its first component child has been passed, so a stack's first child is told apart
	// cheaply in document order without a per-node sibling scan.
	const parentsWithFirstChildSeen: Set<string> = new Set();
	for (const el of root.querySelectorAll<HTMLElement>("[data-page-component]")) {
		const id = el.dataset.componentId;
		if (!id || id === draggedId) continue;
		// The dragged element holds its resting box open, but its content and dnd-kit's layout clone float with the
		// cursor while staying inside it in the DOM, so skip its whole subtree.
		if (excludeSubtreeOf?.contains(el)) continue;
		// The first data-page-component ancestor is the parent, so grab it during the depth walk instead of a second
		// closest() pass.
		let depth = 0;
		let parentEl: HTMLElement | null = null;
		for (let p = el.parentElement; p; p = p.parentElement) {
			if (p.hasAttribute("data-page-component")) {
				if (!parentEl) parentEl = p;
				depth++;
			}
		}
		const parentId = parentEl?.dataset.componentId;
		const isFirstChild = !parentId || !parentsWithFirstChildSeen.has(parentId);
		if (parentId) parentsWithFirstChildSeen.add(parentId);
		const r = el.getBoundingClientRect();
		const raw = getValidPlacementsFromComponent(id, page, definitions, draggedId);
		// An h-box or a full-width type like a Form or a video is never a row cell, so it has no left or right drop.
		// A type that cannot nest may only land at the top level, so a vertical edge inside a nested container renders
		// no slot for it either. Clearing those edges keeps the nearest pick off one that would show nothing.
		const parentIsTopLevel = !parentId || isTopLevelPageVboxContainer(parentId, page);
		const nestingBlocked = !canNest && !parentIsTopLevel;
		const placements = {
			above: nestingBlocked ? false : raw.above,
			below: nestingBlocked ? false : raw.below,
			left: blockHorizontal ? false : raw.left,
			right: blockHorizontal ? false : raw.right,
		};
		// A stretched stack leaves empty space under its last child and above its first, and only a nestable type may
		// land there, so gate the parent lookups on that before touching the DOM.
		let belowFill: InsertionCandidate["belowFill"];
		let aboveFill: InsertionCandidate["aboveFill"];
		if (canNest && (placements.below || (placements.above && isFirstChild))) {
			const parentDir = stringValue(structValue(page.config, parentId ?? ""), "direction");
			const parentRect = parentEl?.getBoundingClientRect();
			// A stacked column appends below its last child. A short leaf cell in a row (parent is an h-box) wraps
			// into a column instead, so its empty space runs from where the cell's own content ends to the row bottom.
			if (placements.below) belowFill = parentDir === DEFAULT_HBOX_CONFIG.direction ? wrapCellFillRegion(el, r) : belowFillRegion(placements.below, parentDir, r.bottom, parentRect);
			if (placements.above && isFirstChild) aboveFill = aboveFillRegion(placements.above, parentDir, r.top, parentRect);
		}
		candidates.push({ id, depth, rect: { top: r.top, bottom: r.bottom, left: r.left, right: r.right }, placements, belowFill, aboveFill });
	}
	return candidates;
}

// Picks the insertion edge nearest the pointer out of every component's rect, depth, and placements.
// Add and drag mode lean greedy, so the cursor still lands on a target when it falls in a dead gap
// between components that no leaf owns.
export function nearestInsertionEdge(candidates: InsertionCandidate[], x: number, y: number, previous?: { id: string; side: InsertionSide }): { id: string; side: InsertionSide } | undefined {
	const TIE_EPS = 4;
	// How far the previously held target may sit behind a new nearest one before it gives way. Crossing between
	// columns lines up many edges at similar distances, so a little stickiness stops the preview from flickering.
	const STICKY_REACH = 12;
	// How far a left or right edge reaches inward, and across into a neighbor, so growing a row into a new
	// column is easy to catch even against a page edge. It only counts when the pointer is level with the cell,
	// so the gaps between rows still read as row inserts and a tall cell keeps its vertical stack zone.
	const HORIZONTAL_REACH = 40;
	type Found = { id: string; side: InsertionSide; dist: number; depth: number; contains: boolean };
	const found: Found[] = [];
	let minDist = Infinity;
	for (const { id, rect, placements, depth, belowFill, aboveFill } of candidates) {
		const contains = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
		const levelWithCell = y >= rect.top && y <= rect.bottom;
		// The point on each edge nearest the cursor (clamped to the edge's extent).
		const cx = Math.max(rect.left, Math.min(x, rect.right));
		const cy = Math.max(rect.top, Math.min(y, rect.bottom));
		const edges: readonly [boolean, number, number, InsertionSide, boolean][] = [
			[placements.above, cx, rect.top, "top", false],
			[placements.below, cx, rect.bottom, "bottom", false],
			[placements.left, rect.left, cy, "left", true],
			[placements.right, rect.right, cy, "right", true],
		];
		for (const [ok, ex, ey, side, horizontal] of edges) {
			if (!ok) continue;
			let dist;
			let edgeContains = contains;
			if (side === "bottom" && belowFill) {
				// The last child of a vertical stack owns the empty space beneath it, so the whole gap appends to
				// the stack rather than snapping to a neighboring row-grow edge. Count the pointer as contained in
				// that gap so the append holds a near-tie against the column's grow edge instead of flipping to grow.
				const fx = Math.max(belowFill.left, Math.min(x, belowFill.right));
				const fy = Math.max(belowFill.top, Math.min(y, belowFill.bottom));
				dist = Math.hypot(x - fx, y - fy);
				edgeContains = contains || (x >= belowFill.left && x <= belowFill.right && y >= belowFill.top && y <= belowFill.bottom);
			} else if (side === "top" && aboveFill) {
				// The first child of a bottom or center aligned stack owns the empty space above it, so the whole gap
				// prepends to the stack the same way the bottom gap appends.
				const fx = Math.max(aboveFill.left, Math.min(x, aboveFill.right));
				const fy = Math.max(aboveFill.top, Math.min(y, rect.top));
				dist = Math.hypot(x - fx, y - fy);
				edgeContains = contains || (x >= aboveFill.left && x <= aboveFill.right && y >= aboveFill.top && y <= rect.top);
			} else {
				dist = Math.hypot(x - ex, y - ey);
				if (horizontal && levelWithCell) dist = Math.max(0, dist - HORIZONTAL_REACH);
			}
			if (dist < minDist) minDist = dist;
			found.push({ id, side, dist, depth, contains: edgeContains });
		}
	}
	// On a near-tie, go by containment. Cursor sitting inside a component (a column item, say) picks the
	// deepest such one. Cursor out in a margin, like above the first row, picks the shallowest top-level
	// target instead. Distance settles whatever is still tied.
	let best: { id: string; side: InsertionSide } | undefined;
	let bestContains = false;
	let bestDepthRank = -Infinity;
	let bestDist = Infinity;
	for (const f of found) {
		if (f.dist > minDist + TIE_EPS) continue;
		const depthRank = f.contains ? f.depth : -f.depth;
		const better = !best || (f.contains && !bestContains) || (f.contains === bestContains && depthRank > bestDepthRank) || (f.contains === bestContains && depthRank === bestDepthRank && f.dist < bestDist);
		if (better) {
			best = { id: f.id, side: f.side };
			bestContains = f.contains;
			bestDepthRank = depthRank;
			bestDist = f.dist;
		}
	}
	// Keep the previous target when it is still nearly as close, so the preview does not jump on every small
	// move across a boundary. It only gives way once a new edge is clearly nearer.
	if (previous) {
		const held = found.find(f => f.id === previous.id && f.side === previous.side);
		if (held && held.dist <= minDist + STICKY_REACH) return { id: previous.id, side: previous.side };
	}
	return best;
}

// Re-projects the resting candidate rects into the current viewport after the page scrolls or the canvas
// pans or zooms. Every component shares one transform, so mapping the page-content box from its snapshot
// frame to now maps every child rect the same way. Width gives the scale since an open gap changes height, not width.
export function reprojectInsertionCandidates(candidates: InsertionCandidate[], ref: { top: number; left: number; width: number }, cur: { top: number; left: number; width: number }): InsertionCandidate[] {
	const scale = ref.width > 0 ? cur.width / ref.width : 1;
	if (Math.abs(cur.top - ref.top) < 0.5 && Math.abs(cur.left - ref.left) < 0.5 && Math.abs(scale - 1) < 0.001) return candidates;
	return candidates.map(c => ({
		...c,
		rect: {
			top: cur.top + (c.rect.top - ref.top) * scale,
			bottom: cur.top + (c.rect.bottom - ref.top) * scale,
			left: cur.left + (c.rect.left - ref.left) * scale,
			right: cur.left + (c.rect.right - ref.left) * scale,
		},
		belowFill: c.belowFill
			? {
					left: cur.left + (c.belowFill.left - ref.left) * scale,
					right: cur.left + (c.belowFill.right - ref.left) * scale,
					top: cur.top + (c.belowFill.top - ref.top) * scale,
					bottom: cur.top + (c.belowFill.bottom - ref.top) * scale,
				}
			: undefined,
		aboveFill: c.aboveFill
			? {
					left: cur.left + (c.aboveFill.left - ref.left) * scale,
					right: cur.left + (c.aboveFill.right - ref.left) * scale,
					top: cur.top + (c.aboveFill.top - ref.top) * scale,
				}
			: undefined,
	}));
}

//util governing what layouts we permit for a given component
export function getValidPlacementsFromComponent(anchorComponentId: string, page: Page, definitions: ComponentDefMap, draggedComponentId?: string): { above: boolean; below: boolean; left: boolean; right: boolean } {
	const loc = locate(page.components, anchorComponentId);
	if (!loc) {
		return { above: false, below: false, left: false, right: false };
	}
	const { node, parent, siblings, index } = loc;
	const draggedSiblingIndex = draggedComponentId ? siblings.findIndex(s => s.id === draggedComponentId) : -1;
	const draggedIsSibling = draggedSiblingIndex >= 0;
	const effectiveSiblingCount = draggedIsSibling ? siblings.length - 1 : siblings.length;
	const effectiveAnchorIndex = draggedIsSibling && draggedSiblingIndex < index ? index - 1 : index;
	const isLast = effectiveAnchorIndex === effectiveSiblingCount - 1;

	const isTopLevelVbox = isTopLevelPageVboxContainer(anchorComponentId, page);
	const parentIsHbox = parent ? isComponentHboxContainer(parent, page) : false;
	const parentIsTopLevelVbox = parent ? isTopLevelPageVboxContainer(parent.id, page) : false;
	const anchorIsContainer = definitions[node.typeUri]?.isContainer ?? false;

	// A leaf cell in an h-box can wrap into a v-box to stack things above and below it. That is our single
	// nesting level. A cell that is already a container never wraps again, so no v-box inside a v-box.
	const isWrapCell = parentIsHbox && !anchorIsContainer;
	// Outside an h-box (top-level rows, inner-vbox children, or a sole root) placement is a plain splice.
	const inVerticalStack = !parentIsHbox && !isTopLevelVbox;

	const above = !isTopLevelVbox && (isWrapCell || inVerticalStack);
	// A wrap cell shows both slots since sibling cells live in different columns and never clash. A plain
	// vertical stack only shows "below" on its last child so neighboring rows don't stack two gaps.
	const below = !isTopLevelVbox && (isWrapCell || (inVerticalStack && isLast));

	// A top-level leaf wraps itself and a new node into a fresh h-box. Any cell already in an h-box, leaf or
	// column, offers a left and right edge so a row can grow by another column and its columns can be reordered
	// by dragging one beside another. The creator decides how many columns a row holds.
	const canWrapIntoNewHbox = allowNesting(node.typeUri) && (!parent || parentIsTopLevelVbox);
	const canGrowHbox = parentIsHbox;
	const horizontal = canWrapIntoNewHbox || canGrowHbox;

	return {
		above,
		below,
		left: horizontal,
		right: horizontal,
	};
}

// Whether a component type may be nested as a layout column or stacked inside an h-box cell.
export function allowNesting(typeUri: string | undefined) {
	if (!typeUri) {
		return false;
	}
	const restrict = RESTRICTED_NESTED_COMPONENTS.some(disallowed => typeUri.includes(disallowed));
	return !restrict;
}

// Whether a component type fills the page width, so it never sits beside another component in a row.
export function isFullWidthComponentType(typeUri: string | undefined): boolean {
	return !!typeUri && FULL_WIDTH_COMPONENTS.some(name => typeUri.includes(name));
}

export function getDepth(componentId: string, page: Page): number {
	const loc = locate(page.components, componentId);
	if (!loc) {
		return -1;
	}

	function childTreeDepth(component: Component): number {
		const children = component.children;
		if (!children?.length) {
			return 0;
		}
		return Math.max(...children.map(child => 1 + childTreeDepth(child)));
	}

	return childTreeDepth(loc.node);
}
